import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getSupports } from '../../../api/endpoints/support';
import { SUPPORT_TYPE_LABELS, type SupportTicket } from '../../../api/types/support';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { SupportFormDialog } from '../components/SupportFormDialog';

/** support/GetSupports 500s against this test account's data regardless of body shape
 *  (see api/types/support.ts) — wired for real; the error simply surfaces via
 *  ApiErrorAlert like any other query failure elsewhere in this app. */
export default function SupportListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({ queryKey: ['support', 'list'], queryFn: () => getSupports() });
  const rows: SupportTicket[] = query.data?.supports ?? [];

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Yardım / Destek Talepleri</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Talep
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Konu</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Tarih</TableCell>
              <TableCell>Durum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Destek talebi bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => row.id && navigate(`/support/${row.id}`)}>
                <TableCell>{row.subject || '—'}</TableCell>
                <TableCell>{row.supportType ? SUPPORT_TYPE_LABELS[row.supportType] : '—'}</TableCell>
                <TableCell>{row.insertedAt ? row.insertedAt.slice(0, 10) : '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.isClosed ? 'default' : 'success'}
                    label={row.isClosed ? 'Kapalı' : 'Açık'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SupportFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
